<template>
  <PolicyChangeForm
    :customerType="customerType"
    :title="title"
    :iconSrc="mortgageeIconPath"
    :steps="steps"
    :current-step="currentStep"
    :email="email"
    :usingPortalForm="true"
    :is-step-valid="isCurrentStepValid"
    @navigate="handleNavigation"
    @reset="resetForm"
    @back="navigateAway"
  >
    <!-- Contact Information Step -->
    <SharedContactInformationStep 
      v-if="currentStep === 1"
      v-model:firstName="firstName"
      v-model:lastName="lastName"
      v-model:policyNumber="policyNumber"
      v-model:phoneNumber="phoneNumber"
      v-model:email="email"
      :customerType="customerType"
      :errors="errors"
      :show-errors="userInteracted"
      :initializing="initializing"
      :is-step-currently-valid="isCurrentStepValid"
      @next="goToNextStep"
    />
    
    <!-- Mortgagee Information Step -->
    <div v-if="currentStep === 2">
      <div class="form-section mb-6">
        <h2 class="text-xl font-semibold text-gray-700 mb-4">Mortgagee Information</h2>
        
        <div class="mb-4">
          <label
            for="mortgagee-action"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Mortgagee Action</label>
          <select
            id="mortgagee-action"
            v-model="mortgageeAction"
            class="input-responsive"
          >
            <option value="">Select action</option>
            <option value="add">Add New Mortgagee</option>
            <option value="update">Update Existing Mortgagee</option>
            <option value="remove">Remove Mortgagee</option>
          </select>
          <p v-if="errors.mortgageeAction" class="text-red-500 text-xs italic">
            {{ errors.mortgageeAction }}
          </p>
        </div>
        
        <div v-if="mortgageeAction === 'add' || mortgageeAction === 'update'" class="mb-4">
          <label
            for="mortgagee-name"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Mortgagee Name</label>
          <input
            id="mortgagee-name"
            v-model="mortgageeName"
            type="text"
            class="input-responsive"
            placeholder="Enter mortgagee company name"
          />
          <p v-if="errors.mortgageeName" class="text-red-500 text-xs italic">
            {{ errors.mortgageeName }}
          </p>
        </div>
        
        <div v-if="mortgageeAction === 'add' || mortgageeAction === 'update'" class="mb-4">
          <label
            for="loan-number"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Loan Number</label>
          <input
            id="loan-number"
            v-model="loanNumber"
            type="text"
            class="input-responsive"
            placeholder="Enter loan number"
          />
          <p v-if="errors.loanNumber" class="text-red-500 text-xs italic">
            {{ errors.loanNumber }}
          </p>
        </div>
        
        <div v-if="mortgageeAction === 'add' || mortgageeAction === 'update'" class="address-section mb-4">
          <h3 class="text-lg font-semibold text-gray-700 mb-3">Mortgagee Address</h3>
          
          <div class="mb-4">
            <label
              for="mortgagee-address"
              class="block text-responsive-sm font-semibold text-gray-700 mb-2"
            >Street Address</label>
            <input
              id="mortgagee-address"
              v-model="mortgageeAddress"
              type="text"
              class="input-responsive"
              placeholder="Enter street address"
            />
            <p v-if="errors.mortgageeAddress" class="text-red-500 text-xs italic">
              {{ errors.mortgageeAddress }}
            </p>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                for="mortgagee-city"
                class="block text-responsive-sm font-semibold text-gray-700 mb-2"
              >City</label>
              <input
                id="mortgagee-city"
                v-model="mortgageeCity"
                type="text"
                class="input-responsive"
                placeholder="City"
              />
              <p v-if="errors.mortgageeCity" class="text-red-500 text-xs italic">
                {{ errors.mortgageeCity }}
              </p>
            </div>
            
            <div>
              <label
                for="mortgagee-state"
                class="block text-responsive-sm font-semibold text-gray-700 mb-2"
              >State</label>
              <select
                id="mortgagee-state"
                v-model="mortgageeState"
                class="input-responsive"
              >
                <option value="">Select state</option>
                <option v-for="state in states" :key="state.code" :value="state.code">
                  {{ state.name }}
                </option>
              </select>
              <p v-if="errors.mortgageeState" class="text-red-500 text-xs italic">
                {{ errors.mortgageeState }}
              </p>
            </div>
            
            <div>
              <label
                for="mortgagee-zip"
                class="block text-responsive-sm font-semibold text-gray-700 mb-2"
              >ZIP Code</label>
              <input
                id="mortgagee-zip"
                v-model="mortgageeZip"
                type="text"
                class="input-responsive"
                placeholder="ZIP Code"
                maxlength="10"
              />
              <p v-if="errors.mortgageeZip" class="text-red-500 text-xs italic">
                {{ errors.mortgageeZip }}
              </p>
            </div>
          </div>
        </div>
        
        <div v-if="mortgageeAction === 'remove'" class="mb-4">
          <label
            for="remove-reason"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Reason for Removal</label>
          <select
            id="remove-reason"
            v-model="removalReason"
            class="input-responsive"
          >
            <option value="">Select reason</option>
            <option value="refinanced">Refinanced Mortgage</option>
            <option value="paid_off">Mortgage Paid Off</option>
            <option value="sold">Property Sold</option>
            <option value="other">Other</option>
          </select>
          <p v-if="errors.removalReason" class="text-red-500 text-xs italic">
            {{ errors.removalReason }}
          </p>
        </div>
        
        <div class="mb-4">
          <label
            for="notes"
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Additional Notes</label>
          <textarea
            id="notes"
            v-model="notes"
            rows="3"
            class="input-responsive"
            placeholder="Enter any additional information..."
          ></textarea>
        </div>
        
        <div class="mb-4">
          <label
            class="block text-responsive-sm font-semibold text-gray-700 mb-2"
          >Supporting Documents</label>
          <DocumentUpload
            v-model="uploadedFile"
            @fileNameChange="handleFileNameChange"
          />
          <p class="text-gray-500 text-xs mt-1">
            Upload mortgage document or proof of change (recommended)
          </p>
        </div>
      </div>
      
      <div class="form-navigation flex justify-between mt-6">
        <button
          @click="goToPreviousStep"
          class="btn-secondary"
        >
          Previous
        </button>
        <button
          @click="goToNextStep"
          class="btn-primary"
          :disabled="!isCurrentStepValid"
        >
          Next
        </button>
      </div>
    </div>
    
    <!-- Review and Submit Step -->
    <SharedEffectiveDateStep
      v-if="currentStep === 3"
      v-model:effectiveDate="effectiveDate"
      v-model:comments="comments"
      :customerType="customerType"
      :isSubmitting="isSubmitting"
      :errors="errors"
      :is-step-currently-valid="isCurrentStepValid"
      @previous="goToPreviousStep"
      @submit="submitForm"
    >
      <template #review-details>
        <div class="review-section mb-6">
          <h3 class="font-semibold text-gray-700 mb-3">Mortgagee Information</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-600">Action:</p>
              <p class="font-medium">{{ mortgageeActionDisplay }}</p>
            </div>
            
            <div v-if="mortgageeAction !== 'remove'">
              <p class="text-sm text-gray-600">Mortgagee Name:</p>
              <p class="font-medium">{{ mortgageeName || 'Not provided' }}</p>
            </div>
            
            <div v-if="mortgageeAction !== 'remove'">
              <p class="text-sm text-gray-600">Loan Number:</p>
              <p class="font-medium">{{ loanNumber || 'Not provided' }}</p>
            </div>
            
            <div v-if="mortgageeAction === 'remove'">
              <p class="text-sm text-gray-600">Removal Reason:</p>
              <p class="font-medium">{{ removalReasonDisplay }}</p>
            </div>
            
            <div v-if="mortgageeAction !== 'remove'">
              <p class="text-sm text-gray-600">Mortgagee Address:</p>
              <p class="font-medium">
                {{ mortgageeAddress }}<br v-if="mortgageeAddress">
                {{ mortgageeCity ? `${mortgageeCity}, ` : '' }}{{ mortgageeState }} {{ mortgageeZip }}
              </p>
            </div>
            
            <div>
              <p class="text-sm text-gray-600">Supporting Document:</p>
              <p class="font-medium">{{ uploadedFile?.name || 'None' }}</p>
            </div>
            
            <div class="col-span-1 md:col-span-2" v-if="notes">
              <p class="text-sm text-gray-600">Additional Notes:</p>
              <p class="font-medium">{{ notes }}</p>
            </div>
          </div>
        </div>
      </template>
    </SharedEffectiveDateStep>
  </PolicyChangeForm>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import PolicyChangeForm from '@/components/common/PolicyChangeForm.vue';
import SharedContactInformationStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedContactInformationStep.vue';
import SharedEffectiveDateStep from '@/components/customerservice/policy-management/forms/shared/steps/SharedEffectiveDateStep.vue';
import DocumentUpload from '@/components/common/DocumentUpload.vue';
import { useMortgageeChangeService } from '@/services/mortgageeChangeService';
import { validateEmail, validatePhoneNumber, validatePolicyNumber, validateZipCode } from '@/utils/validators';
import { validateEffectiveDate } from '@/utils/policyValidators';
import statesData from '@/data/states.js';
import { useRouter } from 'vue-router';
import { getTodayFormatted } from '@/utils/dateUtils';

// Import directly from existing assets that we know work
import policyChangesIcon from '@/assets/images/ui/icons/policyChanges.svg';

export default {
  name: 'MortgageeChangeForm',
  components: {
    PolicyChangeForm,
    SharedContactInformationStep,
    SharedEffectiveDateStep,
    DocumentUpload
  },
  data() {
    return {
      // Use policy-changes.svg temporarily as it's proven to work in other components
      mortgageeIconPath: policyChangesIcon
    };
  },
  props: {
    title: {
      type: String,
      default: 'Mortgagee Change Request'
    },
    customerType: {
      type: String,
      default: 'customer',
      validator: value => ['customer', 'lender'].includes(value)
    }
  },
  setup(props) {
    const mortgageeChangeService = useMortgageeChangeService();
    const router = useRouter();
    
    // Form state
    const currentStep = ref(1);
    const formSubmitted = ref(false);
    const isSubmitting = ref(false);
    const initializing = ref(true); // Add a flag to prevent validation during initialization
    
    // Form data - contact information
    const firstName = ref('');
    const lastName = ref('');
    const policyNumber = ref('');
    const phoneNumber = ref('');
    const email = ref('');
    
    // Form data - mortgagee information
    const mortgageeAction = ref('');
    const mortgageeName = ref('');
    const loanNumber = ref('');
    const mortgageeAddress = ref('');
    const mortgageeCity = ref('');
    const mortgageeState = ref('');
    const mortgageeZip = ref('');
    const removalReason = ref('');
    const notes = ref('');
    const uploadedFile = ref(null);
    const selectedFileName = ref('');
    
    // Form data - submission details
    const effectiveDate = ref(getTodayFormatted()); // Default to today
    const comments = ref('');
    
    // Form validation
    const errors = ref({});
    const userInteracted = ref(false);
    
    // Computed property to determine if the current step is valid
    const isCurrentStepValid = computed(() => {
      if (initializing.value) return true; // Allow navigation during init if needed, or false
      switch (currentStep.value) {
        case 1:
          return validateStep1(false); // Validate without showing errors
        case 2:
          return validateStep2(false); // Validate without showing errors
        case 3:
          return validateStep3(false); // Validate without showing errors
        default:
          return true;
      }
    });
    
    // State data for dropdown
    const states = statesData;
    
    // Computed properties
    const steps = computed(() => [
      { title: 'Contact Information', number: 1 },
      { title: 'Mortgagee Information', number: 2 },
      { title: 'Review and Submit', number: 3 }
    ]);
    
    const mortgageeActionDisplay = computed(() => {
      const actions = {
        'add': 'Add New Mortgagee',
        'update': 'Update Existing Mortgagee',
        'remove': 'Remove Mortgagee'
      };
      return actions[mortgageeAction.value] || 'Not Specified';
    });
    
    const removalReasonDisplay = computed(() => {
      const reasons = {
        'refinanced': 'Refinanced Mortgage',
        'paid_off': 'Mortgage Paid Off',
        'sold': 'Property Sold',
        'other': 'Other'
      };
      return reasons[removalReason.value] || 'Not Specified';
    });
    
    // File handling
    const handleFileNameChange = (fileName) => {
      selectedFileName.value = fileName;
    };
    
    // Add onMounted hook to defer validation on initialization
    onMounted(() => {
      console.log('MortgageeChangeForm mounted, deferring validation');
      initializing.value = true; // Set initializing mode
      
      // Explicitly start with no validation errors - initialize with empty strings for all possible fields
      errors.value = {
        firstName: '',
        lastName: '',
        policyNumber: '',
        phoneNumber: '',
        email: '',
        mortgageeAction: '',
        mortgageeName: '',
        loanNumber: '',
        mortgageeAddress: '',
        mortgageeCity: '',
        mortgageeState: '',
        mortgageeZip: '',
        removalReason: '',
        effectiveDate: ''
      };
      
      // Prevent any validation on mount
      userInteracted.value = false;
      
      console.log('Initial userInteracted value:', userInteracted.value);
      console.log('Initial errors state:', errors.value);
      
      // After a small delay, we're no longer initializing
      setTimeout(() => {
        initializing.value = false;
        console.log('Initialization complete, validation allowed if triggered');
      }, 200);
    });
    
    // Add a watch to reset errors when userInteracted is false
    watch(userInteracted, (newValue) => {
      if (newValue === false) {
        // Reset all errors when userInteracted is set to false
        errors.value = {
          firstName: '',
          lastName: '',
          policyNumber: '',
          phoneNumber: '',
          email: '',
          mortgageeAction: '',
          mortgageeName: '',
          loanNumber: '',
          mortgageeAddress: '',
          mortgageeCity: '',
          mortgageeState: '',
          mortgageeZip: '',
          removalReason: '',
          effectiveDate: ''
        };
        console.log('Reset all errors because userInteracted is false');
      }
    });
    
    // Validation methods
    const validateStep1 = (showErrors = userInteracted.value) => {
      if (initializing.value && showErrors) {
        console.log('Skipping error DISPLAY in validateStep1 during initialization');
        // Still need to return a value if we bail early for showErrors + initializing
        // However, isCurrentStepValid calls with showErrors=false, so this path isn't taken by it.
        // For safety, if we intended to allow initialization to always be valid for UI:
        // return true;
      }
    
      // Always perform the checks to determine actual validity
      const tempErrors = {
        firstName: !firstName.value ? 'First name is required' : '',
        lastName: !lastName.value ? 'Last name is required' : '',
        policyNumber: validatePolicyNumber(policyNumber.value),
        phoneNumber: validatePhoneNumber(phoneNumber.value),
        email: validateEmail(email.value)
      };
    
      // Only update the reactive 'errors' object if we intend to show them
      if (showErrors) {
        console.log('validateStep1 called with showErrors:', showErrors);
        // console.log('Validating Step 1 with data:', firstName.value, lastName.value, policyNumber.value, phoneNumber.value, email.value); // Moved from here
        // console.log('Step 1 tempErrors:', tempErrors); // Moved from here
        errors.value = { ...errors.value, ...tempErrors };
      } else {
          // This log helps see what isCurrentStepValid is working with
          console.log('validateStep1 (for button state) tempErrors:', tempErrors);
      }
    
      return !Object.values(tempErrors).some(error => error); // Return actual validity
    };
    
    const validateStep2 = (showErrors = userInteracted.value) => {
      if (initializing.value && showErrors) {
        console.log('Skipping error DISPLAY in validateStep2 during initialization');
      }

      const tempErrors = {
        mortgageeAction: !mortgageeAction.value ? 'Please select an action' : ''
      };
      
      if (mortgageeAction.value === 'add' || mortgageeAction.value === 'update') {
        tempErrors.mortgageeName = !mortgageeName.value ? 'Mortgagee name is required' : '';
        tempErrors.loanNumber = !loanNumber.value ? 'Loan number is required' : '';
        tempErrors.mortgageeAddress = !mortgageeAddress.value ? 'Street address is required' : '';
        tempErrors.mortgageeCity = !mortgageeCity.value ? 'City is required' : '';
        tempErrors.mortgageeState = !mortgageeState.value ? 'State is required' : '';
        tempErrors.mortgageeZip = validateZipCode(mortgageeZip.value);
      } else if (mortgageeAction.value === 'remove') {
        tempErrors.removalReason = !removalReason.value ? 'Please select a reason for removal' : '';
      }
      
      if (showErrors) {
        console.log('validateStep2 called with showErrors:', showErrors);
        errors.value = { ...errors.value, ...tempErrors };
      } else {
        console.log('validateStep2 (for button state) tempErrors:', tempErrors);
      }
      
      return !Object.values(tempErrors).some(error => error);
    };
    
    const validateStep3 = (showErrors = userInteracted.value) => {
      if (initializing.value && showErrors) {
        console.log('Skipping error DISPLAY in validateStep3 during initialization');
      }

      const tempErrors = {
        effectiveDate: validateEffectiveDate(effectiveDate.value)
      };
      
      if (showErrors) {
        console.log('validateStep3 called with showErrors:', showErrors);
        errors.value = { ...errors.value, ...tempErrors };
      } else {
        console.log('validateStep3 (for button state) tempErrors:', tempErrors);
      }
      
      return !Object.values(tempErrors).some(error => error);
    };
    
    // Navigation methods
    const goToNextStep = () => {
      console.log('Next button clicked, setting userInteracted to true');
      userInteracted.value = true;
      let isValid = false;
      
      switch (currentStep.value) {
        case 1:
          isValid = validateStep1(true);
          break;
        case 2:
          isValid = validateStep2(true);
          break;
        default:
          isValid = true;
      }
      
      if (isValid) {
        currentStep.value += 1;
      }
    };
    
    const goToPreviousStep = () => {
      console.log('Previous button clicked, maintaining userInteracted:', userInteracted.value);
      currentStep.value -= 1;
    };
    
    const handleNavigation = (step) => {
      currentStep.value = step;
    };
    
    // Form submission
    const submitForm = async () => {
      console.log('Form submission initiated, setting userInteracted to true');
      userInteracted.value = true; // Ensure user interaction flag is set to true
      
      console.log('Validating step 3 with explicit showErrors=true');
      if (!validateStep3(true)) { // Explicitly pass true to show errors
        console.log('Validation failed, aborting submission');
        return;
      }
      
      console.log('Validation passed, proceeding with submission');
      isSubmitting.value = true;
      
      try {
        const response = await mortgageeChangeService.submitMortgageeChange({
          contactInfo: {
            firstName: firstName.value,
            lastName: lastName.value,
            policyNumber: policyNumber.value,
            phoneNumber: phoneNumber.value,
            email: email.value
          },
          mortgageeInfo: {
            action: mortgageeAction.value,
            name: mortgageeName.value,
            loanNumber: loanNumber.value,
            address: mortgageeAddress.value,
            city: mortgageeCity.value,
            state: mortgageeState.value,
            zip: mortgageeZip.value,
            removalReason: removalReason.value,
            notes: notes.value,
            file: uploadedFile.value
          },
          submissionDetails: {
            effectiveDate: effectiveDate.value,
            comments: comments.value
          }
        });
        
        formSubmitted.value = true;
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        isSubmitting.value = false;
      }
    };
    
    // Reset form to initial state
    const resetForm = () => {
      firstName.value = '';
      lastName.value = '';
      policyNumber.value = '';
      phoneNumber.value = '';
      email.value = '';
      mortgageeAction.value = '';
      mortgageeName.value = '';
      loanNumber.value = '';
      mortgageeAddress.value = '';
      mortgageeCity.value = '';
      mortgageeState.value = '';
      mortgageeZip.value = '';
      removalReason.value = '';
      notes.value = '';
      uploadedFile.value = null;
      selectedFileName.value = '';
      effectiveDate.value = getTodayFormatted();
      comments.value = '';
      errors.value = {};
      currentStep.value = 1;
      formSubmitted.value = false;
    };
    
    // New method for navigating away
    const navigateAway = () => {
      console.log('[MortgageeChangeForm] navigateAway (back) called.');
      
      // Use the Vue Router to navigate back or to a specific page
      if (router.options.history.state.back) {
        router.back();
      } else {
        router.push('/customerservice/policy-changes');
      }
    };
    
    return {
      // Form state
      currentStep,
      formSubmitted,
      isSubmitting,
      initializing,  // Add initializing flag
      
      // Form data
      firstName,
      lastName,
      policyNumber,
      phoneNumber,
      email,
      mortgageeAction,
      mortgageeActionDisplay,
      mortgageeName,
      loanNumber,
      mortgageeAddress,
      mortgageeCity,
      mortgageeState,
      mortgageeZip,
      removalReason,
      removalReasonDisplay,
      notes,
      uploadedFile,
      selectedFileName,
      effectiveDate,
      comments,
      
      // Reference data
      states,
      
      // Errors
      errors,
      userInteracted,
      isCurrentStepValid, // Expose for template
      
      // Methods
      handleFileNameChange,
      goToNextStep,
      goToPreviousStep,
      handleNavigation,
      submitForm,
      resetForm,
      
      // Add the steps computed property to the return object
      steps,
      
      // New method for navigating away
      navigateAway,
    };
  }
}
</script> 